import React, { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';

import { OPERATION_CATALOG } from '../operationCatalog';
import type {
  ImageLabOperationType,
  OperationResult,
  SourceImageRef,
} from '../../../domain';
import type { ImageRect } from '../canvas/selectionMath';
import type { Size } from '../canvas/canvasMath';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { HeavyCreationOverlay } from '../../../shell/HeavyCreationOverlay';
import { useMobileShell } from '../../../shell/useMobileShell';

/**
 * Heavy-creation operations per spike plan §1.5 — these depend on pointer
 * precision that's painful on phones (mask brushing, multi-step uploads,
 * sketch input). On mobile shell the overlay defers them by default; the user
 * can opt-in via "Continue anyway" if they really want to.
 */
const HEAVY_OPS = new Set<ImageLabOperationType>([
  'Inpaint',
  'Outpaint',
  'RemoveObject',
  'SketchToImage',
  'TryOn',
]);

import { GeneratePanel } from './GeneratePanel';
import { InpaintPanel } from './InpaintPanel';
import { RemoveBackgroundPanel } from './RemoveBackgroundPanel';
import { ImageToImagePanel } from './ImageToImagePanel';
import { UpscalePanel } from './UpscalePanel';
import { EnhancePanel } from './EnhancePanel';
import { VariationsPanel } from './VariationsPanel';
import { RemoveObjectPanel } from './RemoveObjectPanel';
import { OutpaintPanel } from './OutpaintPanel';
import { DenoisePanel } from './DenoisePanel';
import { DeblurPanel } from './DeblurPanel';
import { FaceEnhancePanel } from './FaceEnhancePanel';
import { ColorCorrectPanel } from './ColorCorrectPanel';
import { ColorizePanel } from './ColorizePanel';
import { MultiAnglePanel } from './MultiAnglePanel';
import { StyleTransferPanel } from './StyleTransferPanel';
import { FaceSwapPanel } from './FaceSwapPanel';
import { ColorGradePanel } from './ColorGradePanel';
import { TryOnPanel } from './TryOnPanel';
import { SketchToImagePanel } from './SketchToImagePanel';
import { ZoomPanel } from './ZoomPanel';
import { ConsistencySheetPanel } from './ConsistencySheetPanel';

interface Props {
  kind: ImageLabOperationType;
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  source: SourceImageRef | undefined;
  maskDataUrl: string | null;
  onClearMask?: () => void;
  /** Sketch tablet dataURL (for SketchToImage panel). */
  sketchDataUrl?: string | null;
  onClearSketch?: () => void;
  /** Selection rectangle in image-space (for Zoom panel). */
  selection?: ImageRect | null;
  onClearSelection?: () => void;
  /** Source image dimensions — used by Zoom's rect→params conversion. */
  imageSize?: Size | null;
  /** Humanized active-swatch description (F14b — back-compat for studios
   *  whose palettes use unrecognized kinds like Fashion's `fabric`). Prompt-
   *  having panels merge it into the prompt at submit time. Empty string
   *  when no such selections are active. */
  swatchPrompt?: string;
  /** Structured Studio swatch projection (Bucket 2 — Generic Studio uses
   *  this for style + studio-filter prompt fragments + aspect-ratio
   *  override). Threaded through to every panel; prompt-having panels apply
   *  it to the request, read-only panels render the chip row for context. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

/**
 * Routes the active operation kind to its concrete panel.
 * F12a: Generate / Inpaint / RemoveBackground.
 * F12b: ImageToImage / Upscale / Enhance / Variations / RemoveObject.
 * F12c-1: Outpaint / Denoise / Deblur / FaceEnhance / ColorCorrect /
 *         Colorize / MultiAngle (form-only — no canvas extras).
 * F12c-2: StyleTransfer / FaceSwap / ColorGrade / TryOn (each embeds
 *         ReferenceImageInput for a second image).
 * F12c-3: SketchToImage / Zoom / ConsistencySheet (the canvas-extras
 *         consumers — sketch tablet, selection rect, and the multi-
 *         output op that the grid display will eventually preview).
 *
 * Studio-metadata threading is intentionally asymmetric (audited 2026-05-25,
 * `qa/i2i-payload-audit-2026-05-25.md` §2.5):
 * - `studioParams` (typed projection — prompt fragments + aspect ratio)
 *   passes to the 6 ops where those axes matter: Generate, ImageToImage,
 *   RemoveBackground, Upscale, Enhance, Variations.
 * - `swatchPrompt` (bracket back-compat for non-Generic studios' fabric/
 *   etc. kinds) passes to the 5 ops with a prompt the user might want it
 *   merged into: Inpaint, ImageToImage, Outpaint, TryOn, SketchToImage.
 * - `studioId` + `metadata` (Path C Slice 6 typed metadata) injects
 *   uniformly via `useOperationMutation` for all 22 ops.
 *
 * Ops without a relevant prompt/aspect axis (Denoise, FaceEnhance,
 * ColorCorrect, etc.) don't take `studioParams`/`swatchPrompt` because
 * there's nowhere for them to flow.
 */
export const OperationPanel: React.FC<Props> = ({
  kind,
  ensureSession,
  isCreatingSession,
  source,
  maskDataUrl,
  onClearMask,
  sketchDataUrl,
  onClearSketch,
  selection,
  onClearSelection,
  imageSize,
  swatchPrompt,
  studioParams,
  onResult,
}) => {
  const { isMobileShell } = useMobileShell();
  // Per-op override state: re-affirmed on each operation switch so the user
  // doesn't accidentally inherit "Continue anyway" from a previous heavy op.
  // (Kept in Map rather than a single flag so flipping among heavy ops doesn't
  // cancel an earlier override — UX research can revisit this.)
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  // Defensive reset: if isMobileShell flips off (user rotates / pops to desktop)
  // we don't need the overrides — the desktop path doesn't gate on them.
  useEffect(() => {
    if (!isMobileShell) setOverrides({});
  }, [isMobileShell]);

  const isHeavyMobile = isMobileShell && HEAVY_OPS.has(kind) && !overrides[kind];

  const wrapper = (children: React.ReactNode, label: string) => (
    <Box
      sx={{
        p: 2,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        // Anchor for the absolute-positioned HeavyCreationOverlay below.
        position: 'relative',
        minHeight: isHeavyMobile ? 360 : undefined,
      }}
      aria-label={`${label} options`}
    >
      {children}
      {isHeavyMobile && (
        <HeavyCreationOverlay
          operationLabel={OPERATION_CATALOG[kind]?.name ?? kind}
          onContinueAnyway={() => setOverrides(prev => ({ ...prev, [kind]: true }))}
          // View read-only collapses to the same Continue-anyway path for
          // v3.0 (the canvas already renders the source asset; the user can
          // inspect it without entering the panel form). v3.0.1 polish can
          // diverge if a real read-only sub-mode appears.
          onViewReadOnly={() => setOverrides(prev => ({ ...prev, [kind]: true }))}
        />
      )}
    </Box>
  );

  switch (kind) {
    case 'TextToImage':
      return wrapper(
        <GeneratePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          swatchPrompt={swatchPrompt}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Generate',
      );
    case 'Inpaint':
      return wrapper(
        <InpaintPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          maskDataUrl={maskDataUrl}
          onRequestClearMask={onClearMask}
          swatchPrompt={swatchPrompt}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Inpaint',
      );
    case 'RemoveBackground':
      return wrapper(
        <RemoveBackgroundPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Remove background',
      );
    case 'ImageToImage':
      return wrapper(
        <ImageToImagePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          swatchPrompt={swatchPrompt}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Transform',
      );
    case 'Upscale':
      return wrapper(
        <UpscalePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Upscale',
      );
    case 'Enhance':
      return wrapper(
        <EnhancePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Enhance',
      );
    case 'Variations':
      return wrapper(
        <VariationsPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          studioParams={studioParams}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Variations',
      );
    case 'RemoveObject':
      return wrapper(
        <RemoveObjectPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          maskDataUrl={maskDataUrl}
          onRequestClearMask={onClearMask}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Remove object',
      );
    case 'Outpaint':
      return wrapper(
        <OutpaintPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          swatchPrompt={swatchPrompt}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Expand canvas',
      );
    case 'Denoise':
      return wrapper(
        <DenoisePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Denoise',
      );
    case 'Deblur':
      return wrapper(
        <DeblurPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Deblur',
      );
    case 'FaceEnhance':
      return wrapper(
        <FaceEnhancePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Face enhance',
      );
    case 'ColorCorrect':
      return wrapper(
        <ColorCorrectPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Color correct',
      );
    case 'Colorize':
      return wrapper(
        <ColorizePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Colorize',
      );
    case 'MultiAngle':
      return wrapper(
        <MultiAnglePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Multi-angle',
      );
    case 'StyleTransfer':
      return wrapper(
        <StyleTransferPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Style transfer',
      );
    case 'FaceSwap':
      return wrapper(
        <FaceSwapPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Face swap',
      );
    case 'ColorGrade':
      return wrapper(
        <ColorGradePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Color grade',
      );
    case 'TryOn':
      return wrapper(
        <TryOnPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          swatchPrompt={swatchPrompt}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Try on',
      );
    case 'SketchToImage':
      return wrapper(
        <SketchToImagePanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          sketchDataUrl={sketchDataUrl ?? null}
          onRequestClearSketch={onClearSketch}
          swatchPrompt={swatchPrompt}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Sketch to image',
      );
    case 'Zoom':
      return wrapper(
        <ZoomPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          selection={selection ?? null}
          imageSize={imageSize ?? null}
          onRequestClearSelection={onClearSelection}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Zoom',
      );
    case 'ConsistencySheet':
      return wrapper(
        <ConsistencySheetPanel
          ensureSession={ensureSession}
          isCreatingSession={isCreatingSession}
          source={source}
          onResult={onResult}
        />,
        OPERATION_CATALOG[kind]?.name ?? 'Consistency sheet',
      );
    default:
      return placeholder(kind);
  }
};

const placeholder = (kind: ImageLabOperationType): React.ReactElement => {
  const entry = OPERATION_CATALOG[kind];
  return (
    <Box
      sx={{
        p: 2,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
      aria-label={`${entry?.name ?? kind} options`}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
        <HandymanOutlinedIcon fontSize="small" />
        <Typography variant="caption">
          {entry?.name ?? kind} panel — coming in F12c.
        </Typography>
      </Stack>
    </Box>
  );
};
